import * as THREE from 'three';
import { CelestialBody } from '@/universe/CelestialBody';
import { GLSL_CONSTANTS, GLSL_DITHER, glslFloat } from './common.glsl';
import {
  GLSL_SCATTERING_UNIFORMS,
  MIN_SCALE_HEIGHT,
  ScatteringUniforms,
  createScatteringUniforms,
} from './atmosphereDefaults';

/**
 * Analytic sky, used when the ray-marched atmosphere is disabled.
 *
 * The low quality profile turns off atmospheric ray marching. Without a
 * replacement the daytime sky of a world with a breathable atmosphere renders
 * as pure black, which is not a reduction in fidelity but a wrong picture: the
 * player cannot tell day from night, and the horizon disappears.
 *
 * This shader is the cheap stand-in. It is a closed-form approximation of the
 * same physics: a Rayleigh-tinted gradient whose optical depth grows toward the
 * horizon, a Mie forward-scattering lobe around the star, and a ground haze
 * band. It costs one full-screen-ish pass with no loops, and it uses the body's
 * *authored* scattering coefficients, so Halcyon's sky is blue, Kharis's is
 * butterscotch and Vesper's is orange without any per-planet art.
 */

const VERTEX = /* glsl */ `
varying vec3 vRenderPosition;
void main() {
  vec4 world = modelMatrix * vec4(position, 1.0);
  vRenderPosition = world.xyz;
  gl_Position = projectionMatrix * viewMatrix * world;
}
`;

const FRAGMENT = /* glsl */ `
precision highp float;

${GLSL_CONSTANTS}
${GLSL_DITHER}

varying vec3 vRenderPosition;

${GLSL_SCATTERING_UNIFORMS}
/** Cross-fade with the body's distant impostor. See GLSL_DITHER. */
uniform float uLayerFade;

// Multiple scattering is not modelled. Ignoring it entirely makes a clear sky
// noticeably too dark, because a large fraction of skylight has bounced more
// than once, so a single flat gain stands in for it. Lower than
// AtmosphereMaterial's own MULTI_SCATTER_GAIN (3.0): this is the cheap
// closed-form stand-in used only when ray-marching is disabled, and it is
// tuned against this shader's own (deliberately simplified) scattering model
// rather than shared with the ray marcher.
const float MULTI_SCATTER_GAIN = 1.6;

// Below this fraction of sea-level air density, the sky contributes nothing
// visible; discarding here skips the rest of the shader above the atmosphere.
const float MIN_VISIBLE_AIR_FRACTION = 0.002;

// Guards the 1/radius normalisation of 'up' for a camera passing through the
// planet's centre (never happens in practice, but keeps the divide finite).
const float MIN_RADIUS = 1.0;

// Horizon path-length factor: 1/(cosView*SLOPE + FLOOR). FLOOR keeps the
// lookalike-to-1/cosTheta path length from diverging to infinity exactly at
// the horizon; SLOPE (1 - FLOOR) keeps the zenith (cosView = 1) at exactly
// one scale height.
const float HORIZON_FACTOR_FLOOR = 0.06;
const float HORIZON_FACTOR_SLOPE = 0.94;

// Rayleigh phase function coefficient: the standard 3/(16*PI) normalisation
// folded together with the 1.0 already spent on PI elsewhere, expressed here
// as the flat 0.75 the un-normalised phase shares across every body (the
// division by 16*PI has been absorbed into MULTI_SCATTER_GAIN's tuning).
const float RAYLEIGH_PHASE_COEFF = 0.75;

// Mie forward-scattering lobe: near-isotropic in size but sharply
// forward-peaked toward the star, which is the solar aureole. Clamped so the
// low quality profile cannot produce a saturated white disc where the star
// should be.
const float MIE_LOBE_CLAMP = 10.0;
const float MIE_LOBE_EPS = 1.0e-4;
const float MIE_LOBE_POWER = 1.5;
// Aureole tint: a fixed warm white independent of uSunColor, so a
// deliberately tinted star (see palette work) does not also recolour the
// physically-driven forward-scattering lobe around it.
const vec3 AUREOLE_TINT = vec3(1.0, 0.96, 0.9);

// Twilight reddening: with the star near the horizon its light has already
// crossed the atmosphere edge-on before it scatters, so the blue has been
// stripped out of it and the remaining sky light is red.
const float TWILIGHT_START = 0.28;   // sunUp above which there is no twilight tint
const float TWILIGHT_END = -0.22;    // sunUp below which the tint is fully applied
const vec3 TWILIGHT_COLOR = vec3(1.0, 0.45, 0.2);
const float TWILIGHT_STRENGTH = 2.2; // warm tint gain at full twilight
const float TWILIGHT_VIEW_START = 0.6; // cosView above which twilight tint does not apply
const float TWILIGHT_VIEW_END = 0.0;   // cosView (horizon) where it is fully applied

// Day/night: the sky is only lit where the star actually shines on the air.
const float DAYLIGHT_START = -0.32;  // sunUp where daylight begins ramping in
const float DAYLIGHT_END = 0.12;     // sunUp where it reaches full strength

void main() {
  vec3 rayDir = normalize(vRenderPosition - cameraPosition);
  vec3 fromCentre = cameraPosition - uPlanetCentre;
  float radius = length(fromCentre);
  vec3 up = fromCentre / max(radius, MIN_RADIUS);

  float altitude = max(0.0, radius - uPlanetRadius);
  // Air runs out with altitude, so the sky fades to black on the way up
  // without any explicit switch.
  float airFraction = exp(-altitude / max(uScaleHeight, ${glslFloat(MIN_SCALE_HEIGHT)}));
  if (airFraction < MIN_VISIBLE_AIR_FRACTION) discard;

  vec3 L = normalize(uSunDirection);
  float cosView = dot(rayDir, up);
  float cosSun = dot(rayDir, L);
  float sunUp = dot(up, L);

  // Path length in metres of equivalent sea-level air. A vertical ray through
  // an exponential atmosphere sees one scale height of it; a horizontal ray
  // sees far more, which is why the horizon is pale and the zenith is deep.
  // Using the real scale height here rather than a tuned constant is what keeps
  // the optical depths (and therefore the colour) in physical territory.
  float horizonFactor = 1.0 / max(HORIZON_FACTOR_FLOOR, cosView * HORIZON_FACTOR_SLOPE + HORIZON_FACTOR_FLOOR);
  float pathLength = airFraction * horizonFactor * uScaleHeight * uDensityScale;

  // Rayleigh: strongly wavelength-dependent, so blue saturates first. The
  // (1 - exp(-tau)) form is the single-scattering result and self-limits, which
  // is what turns the zenith blue and the horizon white without a special case.
  vec3 tauRayleigh = uRayleigh * pathLength;
  float rayleighPhase = RAYLEIGH_PHASE_COEFF * (1.0 + cosSun * cosSun);
  vec3 scatter = (1.0 - exp(-tauRayleigh)) * rayleighPhase;

  // Mie: near-isotropic in size but sharply forward-peaked, which is the solar
  // aureole. The lobe is clamped so the low quality profile cannot produce a
  // saturated white disc where the star should be.
  float g = uMieG;
  float g2 = g * g;
  float mieLobe = min(MIE_LOBE_CLAMP, (1.0 - g2) / pow(max(1.0 + g2 - 2.0 * g * cosSun, MIE_LOBE_EPS), MIE_LOBE_POWER));
  float tauMie = uMie * pathLength;
  scatter += AUREOLE_TINT * (1.0 - exp(-tauMie)) * mieLobe;

  // Twilight: with the star near the horizon its light has already crossed the
  // atmosphere edge-on before it scatters, so the blue has been stripped out of
  // it and the remaining sky light is red.
  float twilight = smoothstep(TWILIGHT_START, TWILIGHT_END, sunUp);
  scatter = mix(scatter, scatter * TWILIGHT_COLOR * TWILIGHT_STRENGTH, twilight * smoothstep(TWILIGHT_VIEW_START, TWILIGHT_VIEW_END, cosView));

  // Day/night: the sky is only lit where the star actually shines on the air.
  float daylight = smoothstep(DAYLIGHT_START, DAYLIGHT_END, sunUp);

  vec3 color = scatter * uSunColor * uSunIntensity * daylight * MULTI_SCATTER_GAIN;

  // Additive, not alpha-blended: scattered light is added to whatever lies
  // behind the air. Stars therefore stay where they are and are simply
  // overwhelmed by a bright daytime sky, which is what actually happens.
  // Dithered on the terrain's threshold — see AtmosphereMaterial.
  if (ditheredOut(uLayerFade, gl_FragCoord.xy)) discard;
  gl_FragColor = vec4(color, 1.0);

  // See TerrainMaterial: a ShaderMaterial is exempt from tone mapping and
  // output encoding unless it calls these, and the whole scene has to agree.
  // Half the passes encoded and half not is worse than either choice alone.
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

/** The shared scattering block, plus this shell's impostor cross-fade. */
export interface SkyUniformSet extends ScatteringUniforms {
  /** Cross-fade with the impostor: 1 present, 0 absent. */
  uLayerFade: THREE.IUniform<number>;
}

export function createSkyMaterial(body: CelestialBody): {
  material: THREE.ShaderMaterial;
  uniforms: SkyUniformSet;
} {
  const uniforms: SkyUniformSet = {
    ...createScatteringUniforms(body),
    uLayerFade: { value: 1 },
  };

  const material = new THREE.ShaderMaterial({
    uniforms: uniforms as unknown as Record<string, THREE.IUniform>,
    vertexShader: VERTEX,
    fragmentShader: FRAGMENT,
    side: THREE.BackSide,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: THREE.AdditiveBlending,
  });

  return { material, uniforms };
}
